import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Hero,
  Band,
  Tag,
  PromoBand,
  SectionTitle,
  Testimonial,
  TestimonialGrid,
  // VideoTeaser, — à réactiver avec la section vidéo ci-dessous
  PhoneMockup,
  MediaImage,
  Button,
} from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import { PHOTO_FACADE, PHOTO_JACUZZI, PHOTO_PRODUITS } from './photos'
import PlanityScreen from './PlanityScreen'
import BandeauDeConfiance from '../../components/trust/BandeauDeConfiance'
import { DEFAULT_TESTIMONIALS_PAYLOAD } from '@azoya-spa/contracts'
import { useSiteContent } from '../../hooks/useSiteContent'
import { fusionnerAvecRepli, proprietesVisuel } from './visuels'
import { citation, signature } from './temoignages'
import { bookingCtaClicked } from '../../analytics/events'
import './Accueil.css'

/**
 * Les photos livrées avec le code. Elles restent le repli de la page : si
 * l'API ne répond pas, ou si aucun visuel n'a encore été placé depuis
 * l'administration, la visiteuse voit ces photos-ci plutôt qu'un trou.
 *
 * Les trois sont des photographies authentiques de l'institut : rien à
 * signaler au titre de l'article 50.
 */
const VISUELS_DE_REPLI = {
  hero: PHOTO_FACADE,
  jacuzzi: PHOTO_JACUZZI,
  produits: PHOTO_PRODUITS,
  // Aucune photo embarquée : la section reste masquée tant que
  // l'administration n'en place pas une.
  soinsSignature: null,
}

function useScrollReveal() {
  const rootRef = useRef(null)
  const observerRef = useRef(null)
  // Les éléments déjà confiés à l'observateur, pour ne pas les réinscrire à
  // chaque rendu. WeakSet et non Set : un élément démonté doit rester
  // ramassable par le collecteur.
  const observesRef = useRef(new WeakSet())

  // L'observateur vit aussi longtemps que la page.
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        }
      })
    // Seuil 0 : un pourcentage du bloc serait hors d'atteinte pour un bloc
    // plus haut que l'écran, qui resterait invisible (voir OffresAbonnement).
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' })

    observerRef.current = observer
    return () => {
      observer.disconnect()
      observerRef.current = null
    }
  }, [])

  // Sans tableau de dépendances, donc après CHAQUE rendu — et c'est
  // indispensable, pas une précaution. Les sections alimentées par l'API
  // (témoignages, « Soins signature ») n'existent pas au premier rendu :
  // elles apparaissent quand la réponse arrive. Un balayage unique au montage
  // ne les verrait jamais, et `.accueil-reveal { opacity: 0 }` les laisserait
  // définitivement invisibles faute d'avoir reçu `.is-visible`.
  useEffect(() => {
    const root = rootRef.current
    const observer = observerRef.current
    if (!root || !observer) return

    root.querySelectorAll('[data-reveal]').forEach((el) => {
      if (observesRef.current.has(el)) return
      observesRef.current.add(el)
      observer.observe(el)
    })
  })

  return rootRef
}

function Accueil() {
  const revealRoot = useScrollReveal()
  const visuels = fusionnerAvecRepli(
    useSiteContent('home-visuals', VISUELS_DE_REPLI),
    VISUELS_DE_REPLI,
  )
  // Le filtrage (statut, seuil de note, nombre affiché) est déjà fait par
  // l'API : `items` ne contient que ce qui doit paraître, dans l'ordre voulu.
  const temoignages = useSiteContent('testimonials', DEFAULT_TESTIMONIALS_PAYLOAD)

  return (
    <div className="accueil-page" ref={revealRoot}>
      <div className="accueil-hero-wrap">
        <div className="accueil-reveal" data-reveal>
          <Hero
            image={
              // `eager` : c'est la première image que voit la visiteuse, la
              // différer la ferait apparaître après le texte.
              <MediaImage {...proprietesVisuel(visuels.hero, '100vw')} loading="eager" />
            }
            eyebrow="✦ Institut & Spa · Vaux"
            titleMain="Un institut."
            titleEmphasis="Une parenthèse."
            description="Nichée à dix minutes d'Auxerre, Azoya Spa vous accueille dans un cadre pensé pour ralentir le temps — soins signature, jacuzzi privatif et rituels sur-mesure."
            cta={
              <>
                <Button as={Link} to="/reserver-en-ligne" onClick={() => bookingCtaClicked('accueil_hero')}>
                  Réserver en ligne
                </Button>
                <Button as={Link} to="/services" variant="light">
                  Découvrir les soins
                </Button>
              </>
            }
          />
        </div>
      </div>

      <BandeauDeConfiance className="accueil-reveal accueil-trustbar-wrap" />

      <div className="accueil-reveal" data-reveal>
        <PromoBand tone="dark" watermark="wordmark" quote attribution="— L'équipe Azoya Spa">
          « Un lieu pensé pour vous offrir une véritable parenthèse de bien-être, loin du tumulte
          du quotidien. »
        </PromoBand>
      </div>

      <div className="accueil-reveal" data-reveal>
        <Band
          image={
            // `center` : la zone image d'une Band a deux coins à 50 %, un
            // signalement posé dans un angle y serait rogné.
            <MediaImage {...proprietesVisuel(visuels.jacuzzi, '(min-width: 900px) 50vw, 100vw')} disclosureAlign="center" />
          }
          tag={<Tag variant="primary">Jacuzzi &amp; Hammam privatif</Tag>}
        >
          Profitez de notre espace privatif pour vous relaxer seul ou entre amis, avec sa terrasse
          privative en hiver comme en été.
        </Band>
      </div>

      <div className="accueil-reveal" data-reveal>
        <Band
          reverse
          image={
            <MediaImage {...proprietesVisuel(visuels.produits, '(min-width: 900px) 50vw, 100vw')} disclosureAlign="center" />
          }
          tag={<Tag variant="coral">Produits Baïja</Tag>}
        >
          Retrouvez à l'accueil tous les produits nécessaires pour prolonger le soin à la maison :
          gommages, crèmes, huiles et bougies parfumées.
        </Band>
      </div>

      {/* Section « Soins signature » : elle n'existe que si l'administration a
          placé une photo dans l'emplacement correspondant. Tant qu'il est vide,
          la section reste absente de la page — exactement comme lorsqu'elle
          était commentée ici, mais sans qu'il faille un commit pour la faire
          apparaître. */}
      {visuels.soinsSignature && (
        <div className="accueil-reveal" data-reveal>
          <Band
            tag={<Tag variant="primary">Soins signature</Tag>}
            image={
              <MediaImage
                {...proprietesVisuel(visuels.soinsSignature, '(min-width: 900px) 50vw, 100vw')}
                disclosureAlign="center"
              />
            }
          >
            Un espace feutré, pensé pour les rituels visage et corps sur-mesure, guidés par nos
            praticiennes expertes.
          </Band>
        </div>
      )}

      {/* Section vidéo immersive « univers Azoya » — en attente de complétion : il manque la
          vidéo de l'institut. À réactiver avec l'import VideoTeaser en haut de fichier.
      <div className="accueil-reveal" data-reveal>
        <VideoTeaser
          title="Plongez dans l'univers Azoya"
          subtitle="Une minute pour ressentir l'apaisement des lieux."
          caption="Vidéo immersive : plongez dans l'ambiance de l'institut"
        />
      </div>
      */}

      <div className="accueil-reveal accueil-planity-preview" data-reveal>
        <div className="accueil-planity-preview-text">
          <Tag variant="primary">Réservation en ligne · via Planity</Tag>
          <h2 className="accueil-planity-preview-title">
            Votre prochain soin, à portée de main.
          </h2>
          <p className="accueil-planity-preview-description">
            Choisissez votre soin, votre praticienne et votre créneau en quelques instants sur
            notre page Planity — où que vous soyez, sur mobile comme sur ordinateur.
          </p>
          <Button as={Link} to="/reserver-en-ligne" onClick={() => bookingCtaClicked('accueil_milieu')}>
            Réserver en ligne
          </Button>
        </div>
        {/* Reconstitution du parcours de réservation, pas une capture. Le jour où une vraie
            capture de l'institut existe, elle remplace le children — le châssis ne bouge pas. */}
        <PhoneMockup caption="Illustration du parcours de réservation Azoya Spa">
          <PlanityScreen />
        </PhoneMockup>
      </div>

      {/* Section « Témoignages » : elle n'existe que si l'administration a
          publié au moins un témoignage — et jamais autrement. Les trois
          citations qui vivaient ici étaient inventées, prénoms compris ; un
          avis attribué à une personne qui n'a jamais été cliente est une
          pratique commerciale trompeuse, aggravée sur support numérique. La
          copie de secours embarquée est donc volontairement vide : en cas de
          panne d'API, la section disparaît, elle ne se remplit pas de faux. */}
      {temoignages.items.length > 0 && (
        <div className="accueil-reveal accueil-testimonials-preview" data-reveal>
          <SectionTitle>{temoignages.settings.title}</SectionTitle>
          <TestimonialGrid>
            {temoignages.items.map((temoignage) => (
              <Testimonial
                key={temoignage.id}
                quote={citation(temoignage.quote)}
                name={signature(temoignage.authorName)}
              />
            ))}
          </TestimonialGrid>
        </div>
      )}

      <div className="accueil-reveal" data-reveal>
        <PromoBand
          tone="dark"
          cta={
            <Button as={Link} to="/reserver-en-ligne" variant="light" onClick={() => bookingCtaClicked('accueil_bas')}>
              Réserver en ligne
            </Button>
          }
        >
          Offrez-vous un moment rien qu'à vous. Réservez votre soin dès aujourd'hui et
          laissez-vous guider vers la détente.
        </PromoBand>
      </div>
    </div>
  );
}

export default Accueil;
